import { Check, Crown, ArrowRight } from "lucide-react";
import { Reveal, Chapter } from "@/components/Reveal";
import { scrollToId } from "@/lib/api";

const FREE_SERVICES = [
  {
    name: "Achat de véhicule",
    desc: "Nous trouvons, négocions et livrons votre prochain véhicule.",
  },
  {
    name: "Recherche privée",
    desc: "Une recherche personnalisée dans notre réseau de 600+ véhicules.",
  },
  {
    name: "Vente de votre véhicule",
    desc: "Nous vendons votre véhicule au meilleur prix, sans les tracas.",
  },
  {
    name: "Remplacement de véhicule",
    desc: "Nous orchestrons le passage de votre véhicule actuel vers le prochain.",
  },
];

export default function Tarifs() {
  return (
    <section id="tarifs" data-testid="tarifs-section" className="py-24 md:py-32 lg:py-40">
      <div className="container-x">
        <div className="max-w-3xl">
          <Reveal>
            <Chapter number="05" label="Tarification" />
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.1] tracking-tight">
              Une tarification
              <br />
              <span className="gold-text">simple et transparente.</span>
            </h2>
            <p className="mt-6 text-sm sm:text-base text-slate-400 font-light leading-relaxed">
              Nos services essentiels sont entièrement gratuits. Seule la conciergerie privée — la
              gestion complète de votre automobile — est offerte en forfait, sur mesure.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div
            className="mt-14 rounded-2xl border border-white/10 overflow-hidden"
            data-testid="tarifs-table"
          >
            {FREE_SERVICES.map((s, i) => (
              <div
                key={s.name}
                data-testid={`tarifs-row-${i}`}
                className="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-6 items-center px-6 sm:px-10 py-6 border-b border-white/[0.07] group hover:bg-white/[0.02] transition-colors"
              >
                <div className="sm:col-span-4 flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full border border-gold/30 flex items-center justify-center shrink-0 group-hover:bg-gold/10 transition-colors">
                    <Check size={12} className="text-gold" />
                  </span>
                  <span className="font-display text-lg text-white">{s.name}</span>
                </div>
                <p className="sm:col-span-5 text-sm text-slate-400 font-light">{s.desc}</p>
                <div className="sm:col-span-3 sm:text-right">
                  <span className="inline-flex items-center gap-2 border border-gold/40 bg-gold/[0.07] rounded-full px-4 py-1.5 text-[11px] uppercase tracking-[0.18em] text-gold-light">
                    <span className="w-1.5 h-1.5 rounded-full bg-gold" />
                    Gratuit
                  </span>
                </div>
              </div>
            ))}

            <div
              data-testid="tarifs-row-conciergerie"
              className="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-6 items-center px-6 sm:px-10 py-7 bg-gold/[0.05]"
            >
              <div className="sm:col-span-4 flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-gold/15 border border-gold/50 flex items-center justify-center shrink-0">
                  <Crown size={12} className="text-gold" />
                </span>
                <span className="font-display text-lg text-white">Conciergerie privée</span>
              </div>
              <p className="sm:col-span-5 text-sm text-slate-300 font-light">
                Gestion complète de votre automobile, à la carte ou en forfait annuel. Tarif sur
                mesure selon vos besoins.
              </p>
              <div className="sm:col-span-3 sm:text-right">
                <span className="inline-flex items-center gap-2 border border-gold/60 bg-gold/10 rounded-full px-4 py-1.5 text-[11px] uppercase tracking-[0.18em] text-gold-light">
                  <span className="w-1.5 h-1.5 rounded-full bg-gold" />
                  Forfait
                </span>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.25}>
          <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <button
              data-testid="tarifs-cta-advisor"
              onClick={() => scrollToId("#contact")}
              className="btn-gold"
            >
              Parler à un conseiller <ArrowRight size={16} />
            </button>
            <p className="text-xs text-slate-500 font-light italic max-w-md">
              Parlez à un conseiller pour plus d'informations sur le forfait conciergerie. Aucuns
              frais cachés, jamais.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.3}>
          <div
            className="mt-14 border-t border-white/[0.07] pt-8 max-w-3xl"
            data-testid="tarifs-legal-note"
          >
            <p className="text-xs text-slate-500 font-light leading-relaxed">
              <span className="text-slate-400">* Pourquoi nos services sont-ils gratuits ?</span>
              <br />
              <br />
              La majorité de nos services sont offerts sans frais au consommateur, puisqu'il n'est
              pas nécessaire que le client assume ces coûts. Lorsqu'une transaction automobile est
              conclue, la rémunération du courtier est versée directement par le concessionnaire où
              l'achat ou la location du véhicule a eu lieu. Ainsi, vous profitez d'un
              accompagnement personnalisé, professionnel et sans frais supplémentaires.{" "}
              <em>
                Exception : les services de conciergerie automobile privés sont facturés séparément
                selon les besoins et les forfaits choisis.
              </em>
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
